/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { formatCurrency } from "@helpers/funciones";
import {
  useLazyGetConditionsByIdQuery,
  useLazyGetLoanApplicationByIdQuery,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { setLoanStatus } from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import ButtonDownloadTablaAmortizacion from "@components/ButtonDownloadTablaAmortizacion";

import { STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { ApiError, LoanCondiciones } from "@interfaces/response";
import { useLazyGetLeasingListQuery } from "@api/leasing";
import { LeasingStructure } from "@interfaces/LeasingTypes";
import { LoanApplication } from "@interfaces/store";
import { calculateCreditQuote } from "./helpers";

type FinancialRowItemProps = {
  label: string;
  value: number | string;
  valueSuffix?: string;
  isMobileOrTablet?: boolean;
  isLoading?: boolean;
};

const FinancialRowItem = ({ label, value, valueSuffix, isMobileOrTablet, isLoading }: FinancialRowItemProps) => {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
      }}
    >
      <Typography
        variant={isMobileOrTablet ? "body2" : "body1"}
        color="primary.main"
        fontWeight={500}
        textAlign="center"
      >
        {label}
      </Typography>
      {isLoading ? (
        <Skeleton variant="text" animation="wave" width="70%" sx={{ fontSize: isMobileOrTablet ? "14px" : "16px" }} />
      ) : (
        <Typography
          variant={isMobileOrTablet ? "body2" : "body1"}
          color="primary.dark"
          fontWeight={500}
          textAlign="center"
        >
          {value}
          {valueSuffix && (
            <Typography
              component="span"
              variant={isMobileOrTablet ? "body3" : "body2"}
              color="primary.dark"
              fontWeight={500}
            >
              {" "}
              {valueSuffix}
            </Typography>
          )}
        </Typography>
      )}
    </Box>
  );
};

const OfertaVivemoto = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [getLeasingList] = useLazyGetLeasingListQuery();
  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState<boolean>(false);
  const [condition, setCondition] = useState<LoanCondiciones | null>(null);
  const [leasingData, setLeasingData] = useState<Array<LeasingStructure>>([]);
  const [loanApplication, setApplicationData] = useState<LoanApplication | null>(null);

  const [insurance, setInsurance] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [advanceAmount, setAdvanceAmount] = useState<number>(0);
  const [financeAmount, setFinanceAmount] = useState<number>(0);
  const [monthlyPayment, setMonthlyPayment] = useState<number>(0);

  const handleOnClickUpdateConditionLoan = async () => {
    try {
      setIsLoading(true);

      const { check_list, informacion_documentos_pfae } = STATUS_CATALOG;

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: check_list.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      dispatch(setLoanStatus(check_list.code));

      if (application.tax_system_type == TAX_SYSTEM_TYPE.PM.code) {
        navigate(check_list.url);
      } else {
        navigate(informacion_documentos_pfae.url);
      }
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsFetching(true);
        const [conditions, leasing, loanApplication] = await Promise.all([
          getConditions(application.id).unwrap(),
          getLeasingList(String(application.id)).unwrap(),
          getLoanApplication(application.id).unwrap(),
        ]);

        setApplicationData(loanApplication);
        const activeCondition = conditions.find((item) => item.active);
        if (activeCondition) {
          setCondition(activeCondition);
        }

        let insuranceValue = 0;
        if (leasing) {
          setLeasingData(leasing);
          const insurancevalue = leasing[0]?.details?.insurance ?? 0;
          const platesFee = leasing[0]?.details?.plates_fee ?? 0;
          const ownershipTax = leasing[0]?.details?.ownership_tax ?? 0;
          insuranceValue = insurancevalue + platesFee + ownershipTax;
          const totalAmountValue = insuranceValue + (leasing[0]?.estimated_value ?? 0);
          const advanceAmountValue = (totalAmountValue * (activeCondition?.selected_advance_percentage || 0)) / 100;
          const financeAmountValue = totalAmountValue - advanceAmountValue;

          setInsurance(insuranceValue);
          setTotalAmount(totalAmountValue);
          setAdvanceAmount(advanceAmountValue);
          setFinanceAmount(financeAmountValue);
        }
      } catch (error) {
        console.log("Ha ocurrido un error", error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, []);

  const handleDownloadTable = async () => {
    setIsGeneratingDocument(true);
    // TODO: conectar la generación real de la tabla de amortización
    setIsGeneratingDocument(false);
  };

  const calculateAmounts = () => {
    if (!condition) {
      return;
    }

    const monto = financeAmount;
    const plazo = condition?.selected_term ?? loanApplication?.approved_term;
    const rawRate = condition?.interest_rate ?? loanApplication?.interest_rate;

    if (!monto || !plazo || !rawRate) {
      return;
    }

    // la tasa puede venir como fracción (0.4) o como porcentaje (40)
    const tasa = rawRate <= 1 ? rawRate * 100 : rawRate;

    // financeAmount ya tiene descontado el enganche
    const { monthlyPayment } = calculateCreditQuote({ unitValue: monto, termMonths: plazo, annualRate: tasa });
    setMonthlyPayment(Number(monthlyPayment.toFixed(2)));
  };

  useEffect(() => {
    calculateAmounts();
  }, [condition, financeAmount]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={12} mt={5} sx={{ position: "relative" }}>
        <InactivityHandler />
        <Container maxWidth="sm">
          <Box
            sx={{
              mb: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "100%", textAlign: "left", mb: 2.5 }}>
              <Box sx={{ borderLeft: "2px solid", borderColor: "primary.main", pl: 1.5 }}>
                <Typography
                  variant="caption"
                  color="primary.main"
                  fontWeight={theme?.palette?.fontWeights?.bold}
                  sx={{ letterSpacing: "0.5px", textTransform: "uppercase", display: "block" }}
                >
                  Oferta · Pre-autorizado
                </Typography>

                <Typography
                  variant="h6"
                  color="primary.dark"
                  fontWeight={theme?.palette?.fontWeights?.bold}
                  fontSize={{ xs: "18px", sm: "20px" }}
                  lineHeight={1.3}
                >
                  ¡Tu crédito para moto está pre-autorizado!
                </Typography>
                <Typography variant="body2" color="primary.dark" sx={{ opacity: 0.75, lineHeight: 1.5, mt: 0.75 }}>
                  Aprobamos el financiamiento para tu moto nueva. Revisa el precio, el plazo y el pago mensual estimado,
                  y descarga tu tabla de amortización.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                width: "100%",
                background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(theme.palette.primary.base, 0.4)} 55%, ${theme.palette.common.white} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                borderRadius: "20px",
                boxShadow: `0 4px 16px ${alpha(theme.palette.primary.dark, 0.08)}`,
                mb: 2.5,
              }}
            >
              <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" mt={4}>
                <FinancialRowItem
                  label="Precio de la motocicleta"
                  value={formatCurrency(leasingData[0]?.estimated_value || 0)}
                  valueSuffix=" MXN"
                  isMobileOrTablet={isMobileOrTablet}
                  isLoading={isFetching}
                />
                <Divider orientation="vertical" flexItem sx={{ borderColor: "primary.main", borderWidth: "0.5" }} />
                <FinancialRowItem
                  label="Plazo"
                  value={String(condition?.selected_term || 0)}
                  valueSuffix=" meses"
                  isMobileOrTablet={isMobileOrTablet}
                  isLoading={isFetching}
                />
              </Box>

              <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" mt={4}>
                <FinancialRowItem
                  label="Seguro, placas y tenencia"
                  value={formatCurrency(insurance || 0)}
                  valueSuffix=" MXN"
                  isMobileOrTablet={isMobileOrTablet}
                  isLoading={isFetching}
                />
                <Divider orientation="vertical" flexItem sx={{ borderColor: "primary.main", borderWidth: "0.5" }} />
                <FinancialRowItem
                  label="Costo Total"
                  value={formatCurrency(totalAmount || 0)}
                  valueSuffix=" MXN"
                  isMobileOrTablet={isMobileOrTablet}
                  isLoading={isFetching}
                />
              </Box>

              <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" mt={4}>
                <FinancialRowItem
                  label={`Enganche (${condition?.selected_advance_percentage}%)`}
                  value={formatCurrency(advanceAmount || 0)}
                  valueSuffix=" MXN"
                  isMobileOrTablet={isMobileOrTablet}
                  isLoading={isFetching}
                />
                <Divider orientation="vertical" flexItem sx={{ borderColor: "primary.main", borderWidth: "0.5" }} />
                <FinancialRowItem
                  label="Monto a Financiar"
                  value={formatCurrency(financeAmount || 0)}
                  valueSuffix=" MXN"
                  isMobileOrTablet={isMobileOrTablet}
                  isLoading={isFetching}
                />
              </Box>

              <Box display="flex" justifyContent="center" mt={4} mb={4}>
                <Box width={isMobileOrTablet ? "100%" : "45%"}>
                  <FinancialRowItem
                    label="Pago Mensual"
                    value={formatCurrency(monthlyPayment || 0)}
                    valueSuffix=" MXN"
                    isMobileOrTablet={isMobileOrTablet}
                    isLoading={isFetching}
                  />
                </Box>
              </Box>
            </Box>

            <ButtonDownloadTablaAmortizacion disabled={isGeneratingDocument} onClick={handleDownloadTable} />

            <Box display="flex" justifyContent="center" width="100%" mt={isMobileOrTablet ? 2 : 3} mb={2}>
              <Button variant="default" onClick={handleOnClickUpdateConditionLoan} disabled={isLoading || isFetching}>
                Continuar
              </Button>
            </Box>
          </Box>
        </Container>
      </Grid>
    </Grid>
  );
};

export default OfertaVivemoto;
